import React, { useState, useEffect, useRef } from 'react';
import { Header } from './ui/components/Header';
import { StepWizard } from './ui/components/StepWizard';
import { LiveView } from './ui/views/LiveView';
import { TrajectoryView } from './ui/views/TrajectoryView';
import { AcousticView } from './ui/views/AcousticView';
import { ThreeDView } from './ui/views/ThreeDView';
import { DataView } from './ui/views/DataView';
import { DiagnosticsView } from './ui/views/DiagnosticsView';

import { CameraCaptureSensor, CameraConfig } from './core/sensors/camera/capture';
import { ColorMarkerTrackerSensor, TENNIS_BALL_PRESET } from './core/sensors/tracker/color-marker';
import { MicrophoneFeaturesSensor } from './core/sensors/audio/microphone-features';
import { AcousticPositionFusionEngine } from './core/processors/fusion/acoustic-position';

import { ColorMarkerConfig, TrackingResult, BoundingBox } from './core/types/tracking';
import { AudioSensorConfig, AudioFeatureWindow } from './core/types/audio';
import { FusedRecord } from './core/types/fusion';
import { DeviceInfo, SessionMetadata } from './core/types/session';
import { SensorStatus } from './core/types/sensor';
import { exportVisualCsv, exportAudioCsv, exportFusedCsv, exportSessionJson, downloadFile } from './utils/export';

export const App: React.FC = () => {
  const [isStereoMode, setIsStereoMode] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'live' | 'trajectory' | 'acoustic' | '3d' | 'data' | 'diagnostics'>('live');
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [showMask, setShowMask] = useState<boolean>(false);

  // Device lists
  const [videoDevices, setVideoDevices] = useState<DeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<DeviceInfo[]>([]);

  // Configs
  const [cameraConfigA, setCameraConfigA] = useState<CameraConfig>({ idealWidth: 640, idealHeight: 480, idealFrameRate: 30 });
  const [cameraConfigB, setCameraConfigB] = useState<CameraConfig>({ idealWidth: 640, idealHeight: 480, idealFrameRate: 30 });
  const [markerConfigA, setMarkerConfigA] = useState<ColorMarkerConfig>({ ...TENNIS_BALL_PRESET });
  const [markerConfigB, setMarkerConfigB] = useState<ColorMarkerConfig>({ ...TENNIS_BALL_PRESET });
  const [audioConfig, setAudioConfig] = useState<AudioSensorConfig>({
    windowSizeMs: 100,
    sampleRate: 44100,
    fftSize: 2048,
    requestEchoCancellation: false,
    requestAutoGainControl: false,
    requestNoiseSuppression: false,
  });

  // Sensor Instances & References
  const camSensorARef = useRef<CameraCaptureSensor | null>(null);
  const camSensorBRef = useRef<CameraCaptureSensor | null>(null);
  const trackerARef = useRef<ColorMarkerTrackerSensor | null>(null);
  const trackerBRef = useRef<ColorMarkerTrackerSensor | null>(null);
  const micSensorRef = useRef<MicrophoneFeaturesSensor | null>(null);
  const fusionEngineRef = useRef<AcousticPositionFusionEngine>(new AcousticPositionFusionEngine());

  // Real-time states
  const [latestTrackingA, setLatestTrackingA] = useState<TrackingResult | null>(null);
  const [latestTrackingB, setLatestTrackingB] = useState<TrackingResult | null>(null);
  const [latestAudio, setLatestAudio] = useState<AudioFeatureWindow | null>(null);
  const [maskImageDataA, setMaskImageDataA] = useState<ImageData | undefined>(undefined);
  const [maskImageDataB, setMaskImageDataB] = useState<ImageData | undefined>(undefined);
  const [fusedRecords, setFusedRecords] = useState<FusedRecord[]>([]);
  const [trackingHistoryA, setTrackingHistoryA] = useState<TrackingResult[]>([]);
  const [audioHistory, setAudioHistory] = useState<AudioFeatureWindow[]>([]);

  const [systemStatus, setSystemStatus] = useState<SensorStatus>('idle');
  const [runId, setRunId] = useState<string>(`run_${Date.now()}`);

  const refreshDevices = async () => {
    try {
      const vDevs = await CameraCaptureSensor.enumerateVideoDevices();
      const aDevs = await MicrophoneFeaturesSensor.enumerateAudioDevices();
      setVideoDevices(vDevs);
      setAudioDevices(aDevs);
    } catch (e: any) {
      console.warn('Device enumeration failed:', e.message);
    }
  };

  useEffect(() => {
    refreshDevices();
  }, []);

  // Handle Start / Stop Capture Recording
  const startCapture = async () => {
    try {
      const newRunId = `run_${Date.now()}`;
      setRunId(newRunId);
      setFusedRecords([]);
      setTrackingHistoryA([]);
      setAudioHistory([]);
      fusionEngineRef.current.setRunId(newRunId, isStereoMode);

      // 1. Initialize Camera A & Tracker A
      camSensorARef.current = new CameraCaptureSensor('camera.capture.a', 'Camera A');
      camSensorARef.current.configure(cameraConfigA);

      trackerARef.current = new ColorMarkerTrackerSensor('tracker.color-marker.a', 'Tracker A');
      trackerARef.current.configure(markerConfigA);
      await trackerARef.current.start();

      trackerARef.current.onData((result, mask) => {
        setLatestTrackingA(result);
        if (mask) setMaskImageDataA(mask);
        fusionEngineRef.current.pushCameraA(result);
        setTrackingHistoryA((prev) => [...prev.slice(-1000), result]);
      });

      camSensorARef.current.onData((bitmap) => {
        if (trackerARef.current) {
          trackerARef.current.processFrame(bitmap, showMask);
        } else {
          bitmap.close();
        }
      });

      await camSensorARef.current.start();

      // Refresh devices list now that permission is granted, so full labels are revealed
      await refreshDevices();

      // 2. Camera B (if stereo mode)
      if (isStereoMode && cameraConfigB.deviceId) {
        try {
          camSensorBRef.current = new CameraCaptureSensor('camera.capture.b', 'Camera B');
          camSensorBRef.current.configure(cameraConfigB);
          trackerBRef.current = new ColorMarkerTrackerSensor('tracker.color-marker.b', 'Tracker B');
          trackerBRef.current.configure(markerConfigB);
          await trackerBRef.current.start();

          trackerBRef.current.onData((result, mask) => {
            setLatestTrackingB(result);
            if (mask) setMaskImageDataB(mask);
            fusionEngineRef.current.pushCameraB(result);
          });

          camSensorBRef.current.onData((bitmap) => {
            if (trackerBRef.current) {
              trackerBRef.current.processFrame(bitmap, showMask);
            } else {
              bitmap.close();
            }
          });

          await camSensorBRef.current.start();
        } catch (err: any) {
          console.warn('Camera B failed, degrading to single-cam mode:', err);
          setSystemStatus('degraded');
        }
      }

      // 3. Audio Microphone Sensor
      micSensorRef.current = new MicrophoneFeaturesSensor('audio.microphone-features', 'Microphone');
      micSensorRef.current.configure(audioConfig);

      micSensorRef.current.onData((audioWin) => {
        setLatestAudio(audioWin);
        setAudioHistory((prev) => [...prev.slice(-1000), audioWin]);

        // Process Fusion
        const fused = fusionEngineRef.current.processAudioWindow(audioWin);
        setFusedRecords((prev) => [...prev, fused]);
      });

      await micSensorRef.current.start();

      setIsRecording(true);
      if (systemStatus !== 'degraded') {
        setSystemStatus('running');
      }
    } catch (err: any) {
      alert(`启动采集失败: ${err.message}`);
      setSystemStatus('error');
      stopCapture();
    }
  };

  const stopCapture = async () => {
    setIsRecording(false);
    if (camSensorARef.current) {
      await camSensorARef.current.stop();
      camSensorARef.current = null;
    }
    if (camSensorBRef.current) {
      await camSensorBRef.current.stop();
      camSensorBRef.current = null;
    }
    if (trackerARef.current) {
      await trackerARef.current.stop();
      trackerARef.current = null;
    }
    if (trackerBRef.current) {
      await trackerBRef.current.stop();
      trackerBRef.current = null;
    }
    if (micSensorRef.current) {
      await micSensorRef.current.stop();
      micSensorRef.current = null;
    }
    setSystemStatus('idle');
  };

  // ROI auto estimation on selection for Cam A
  const handleSelectRoiA = (roi: BoundingBox) => {
    if (trackerARef.current && camSensorARef.current) {
      const videoEl = camSensorARef.current.getVideoElement();
      if (videoEl && videoEl.readyState >= 2) {
        createImageBitmap(videoEl).then((bm) => {
          const bounds = trackerARef.current?.estimateHsvFromSelectedRoi(bm, roi);
          if (bounds) {
            setMarkerConfigA((prev) => ({
              ...prev,
              hsvLower: bounds.lower,
              hsvUpper: bounds.upper,
            }));
            alert(`[Cam A] 已根据框选区域自动估算 HSV: Lower [${bounds.lower.join(', ')}], Upper [${bounds.upper.join(', ')}]`);
          }
          bm.close();
        });
      }
    }
  };

  // ROI auto estimation on selection for Cam B
  const handleSelectRoiB = (roi: BoundingBox) => {
    if (trackerBRef.current && camSensorBRef.current) {
      const videoEl = camSensorBRef.current.getVideoElement();
      if (videoEl && videoEl.readyState >= 2) {
        createImageBitmap(videoEl).then((bm) => {
          const bounds = trackerBRef.current?.estimateHsvFromSelectedRoi(bm, roi);
          if (bounds) {
            setMarkerConfigB((prev) => ({
              ...prev,
              hsvLower: bounds.lower,
              hsvUpper: bounds.upper,
            }));
            alert(`[Cam B] 已根据框选区域自动估算 HSV: Lower [${bounds.lower.join(', ')}], Upper [${bounds.upper.join(', ')}]`);
          }
          bm.close();
        });
      }
    }
  };

  const handleExportCsv = (type: 'visual' | 'audio' | 'fused' | 'json') => {
    if (type === 'visual') {
      const csv = exportVisualCsv(trackingHistoryA, runId);
      downloadFile(csv, `${runId}_visual_tracking.csv`);
    } else if (type === 'audio') {
      const csv = exportAudioCsv(audioHistory, runId);
      downloadFile(csv, `${runId}_audio_features.csv`);
    } else if (type === 'fused') {
      const csv = exportFusedCsv(fusedRecords);
      downloadFile(csv, `${runId}_fused_experiment.csv`);
    } else if (type === 'json') {
      const metadata: SessionMetadata = {
        schemaVersion: '1.0.0',
        appVersion: '1.0.0',
        runId,
        startTime: new Date().toISOString(),
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        markerConfigA,
        audioConfig,
        fusionConfig: { matchToleranceMs: 150, primaryRhythm: 'audio' },
        stats: {
          totalAudioWindows: audioHistory.length,
          totalFramesA: trackingHistoryA.length,
          totalFramesB: 0,
          matchedRecords: fusedRecords.filter((r) => r.matchStatus === 'matched').length,
          partialRecords: fusedRecords.filter((r) => r.matchStatus === 'partial').length,
          unmatchedRecords: fusedRecords.filter((r) => r.matchStatus === 'unmatched').length,
          lostFramesA: trackingHistoryA.filter((r) => r.status === 'lost').length,
          lostFramesB: 0,
          droppedFramesA: 0,
          droppedFramesB: 0,
          avgFpsA: 30,
          avgFpsB: 0,
          avgLatencyMs: 2.5,
        },
      };
      const jsonStr = exportSessionJson(metadata, fusedRecords);
      downloadFile(jsonStr, `${runId}_session.json`, 'application/json');
    }
  };

  return (
    <div className="app-container">
      <Header
        status={systemStatus}
        isStereoMode={isStereoMode}
        isRecording={isRecording}
        onToggleStereoMode={(s) => setIsStereoMode(s)}
        onNewExperiment={() => {
          stopCapture();
          setFusedRecords([]);
        }}
        onStartRecording={startCapture}
        onStopRecording={stopCapture}
        onExport={() => handleExportCsv('fused')}
        onOpenHelp={() =>
          alert('双摄颜色标记与声场同步追踪系统: 选择摄像头与麦克风后框选黄绿色网球，即可开始实时二维轨迹与声音强度融合采集。')
        }
      />

      <div className="app-body">
        <StepWizard
          videoDevices={videoDevices}
          audioDevices={audioDevices}
          cameraConfigA={cameraConfigA}
          cameraConfigB={cameraConfigB}
          markerConfigA={markerConfigA}
          markerConfigB={markerConfigB}
          audioConfig={audioConfig}
          isStereoMode={isStereoMode}
          onUpdateCameraA={(cfg) => setCameraConfigA((prev) => ({ ...prev, ...cfg }))}
          onUpdateCameraB={(cfg) => setCameraConfigB((prev) => ({ ...prev, ...cfg }))}
          onUpdateMarkerA={(cfg) => {
            setMarkerConfigA((prev) => ({ ...prev, ...cfg }));
            trackerARef.current?.configure(cfg);
          }}
          onUpdateMarkerB={(cfg) => {
            setMarkerConfigB((prev) => ({ ...prev, ...cfg }));
            trackerBRef.current?.configure(cfg);
          }}
          onUpdateAudio={(cfg) => setAudioConfig((prev) => ({ ...prev, ...cfg }))}
          onCopyMarkerAToB={() => setMarkerConfigB({ ...markerConfigA })}
          onRefreshDevices={refreshDevices}
          onExportCsv={handleExportCsv}
        />

        <div className="main-content">
          <div className="tabs-nav">
            {[
              { id: 'live', label: '实时 Live' },
              { id: 'trajectory', label: '轨迹 2D Trajectory' },
              { id: 'acoustic', label: '声音 Acoustic Field' },
              { id: '3d', label: '三维 3D Trajectory' },
              { id: 'data', label: '数据 Data Table' },
              { id: 'diagnostics', label: '诊断 Diagnostics' },
            ].map((tab) => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id as any)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-view-container">
            {activeTab === 'live' && (
              <LiveView
                isStereoMode={isStereoMode}
                trackingResultA={latestTrackingA}
                trackingResultB={latestTrackingB}
                audioFeatures={latestAudio}
                showMask={showMask}
                onToggleMask={() => setShowMask(!showMask)}
                videoElementA={camSensorARef.current?.getVideoElement() || null}
                videoElementB={camSensorBRef.current?.getVideoElement() || null}
                maskImageDataA={maskImageDataA}
                maskImageDataB={maskImageDataB}
                onSelectRoiA={handleSelectRoiA}
                onSelectRoiB={handleSelectRoiB}
              />
            )}

            {activeTab === 'trajectory' && <TrajectoryView records={fusedRecords} />}

            {activeTab === 'acoustic' && <AcousticView records={fusedRecords} />}

            {activeTab === '3d' && <ThreeDView records={fusedRecords} />}

            {activeTab === 'data' && <DataView records={fusedRecords} />}

            {activeTab === 'diagnostics' && (
              <DiagnosticsView
                healthA={camSensorARef.current?.health()}
                healthAudio={micSensorRef.current?.health()}
                actualFpsA={camSensorARef.current?.getDeviceInfo().actualFrameRate}
                audioSettings={micSensorRef.current?.getActualSettings()}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
